import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef } from "react";
import { PIXIWEB_HTML } from "@/lib/pixiweb-design";
import { getPublicPortfolio } from "@/lib/portfolio.functions";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Pixiweb – Webdesign, Entwicklung & SEO für moderne Marken" },
      { name: "description", content: "Pixiweb gestaltet und entwickelt moderne, schnelle und SEO-optimierte Websites mit dem Wow-Effekt. Webdesign, Webentwicklung, E-Commerce und SEO aus einer Hand." },
      { property: "og:title", content: "Pixiweb – Webdesign, das begeistert und verkauft" },
      { property: "og:description", content: "Moderne, schnelle und SEO-optimierte Websites – maßgeschneidert für deine Marke." },
      { property: "og:url", content: "https://pixiweb.de/" },
    ],
    links: [{ rel: "canonical", href: "https://pixiweb.de/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Was kostet eine Website?", acceptedAnswer: { "@type": "Answer", text: "Das lässt sich pauschal nicht sagen – jedes Projekt ist individuell. Umfang, Funktionen, Designtiefe und Inhalte beeinflussen den Aufwand. Nach einem kurzen Gespräch erhältst du ein transparentes, auf dein Projekt zugeschnittenes Angebot – ohne versteckte Kosten." } },
            { "@type": "Question", name: "Wie lange dauert ein Projekt?", acceptedAnswer: { "@type": "Answer", text: "Eine typische Website ist in 3–5 Wochen live. Größere Projekte planen wir gemeinsam. Wichtig ist mir ein verlässlicher Zeitplan mit klaren Meilensteinen." } },
            { "@type": "Question", name: "Arbeitest du remote oder vor Ort?", acceptedAnswer: { "@type": "Answer", text: "Ich arbeite vollständig remote und betreue Kunden im gesamten deutschsprachigen Raum. Per Video-Call, E-Mail und geteiltem Projektboard bleiben wir eng in Kontakt." } },
            { "@type": "Question", name: "Übernimmst du auch die SEO?", acceptedAnswer: { "@type": "Answer", text: "Ja. Jede Website wird technisch für Suchmaschinen optimiert – schnelle Ladezeiten, saubere Struktur und Meta-Daten inklusive. Auf Wunsch betreue ich auch laufende SEO-Maßnahmen." } },
            { "@type": "Question", name: "Was brauchst du von mir zum Start?", acceptedAnswer: { "@type": "Answer", text: "Eine grobe Vorstellung von Ziel und Zielgruppe sowie vorhandene Inhalte wie Texte, Logo und Bilder. Fehlt etwas, unterstütze ich dich gerne dabei oder vermittle Profis." } },
            { "@type": "Question", name: "Bietest du auch Wartung an?", acceptedAnswer: { "@type": "Answer", text: "Mit einem Wartungspaket kümmere ich mich um Updates, Backups, Sicherheit und kleine Änderungen – damit deine Website dauerhaft sicher und aktuell bleibt." } },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Webdesign, Webentwicklung, Software-Entwicklung und SEO",
          provider: { "@id": "https://pixiweb.de/#organization" },
          areaServed: { "@type": "Country", name: "Deutschland" },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Leistungen",
            itemListElement: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Webdesign" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Webentwicklung" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Software-Entwicklung" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "SEO-Optimierung" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "E-Commerce" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Branding" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Support & Wartung" } },
            ],
          },
        }),
      },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  const ref = useRef<HTMLDivElement>(null);
  const fetchPortfolio = useServerFn(getPublicPortfolio);


  useEffect(() => {
    const D = document;

    // Reveal on scroll
    const revealEls = Array.from(D.querySelectorAll<HTMLElement>("[data-reveal]"));
    const checkReveal = () => {
      const vh = window.innerHeight || 0;
      for (const el of revealEls) {
        if (el.classList.contains("in")) continue;
        const r = el.getBoundingClientRect();
        if (r.top < vh * 0.9 && r.bottom > 0) {
          const sibs = el.parentElement
            ? Array.from(el.parentElement.querySelectorAll<HTMLElement>(":scope > [data-reveal]"))
            : [el];
          const idx = Math.max(0, sibs.indexOf(el));
          el.style.transitionDelay = Math.min(idx * 70, 420) + "ms";
          el.classList.add("in");
        }
      }
    };
    window.addEventListener("scroll", checkReveal, { passive: true });
    window.addEventListener("resize", checkReveal);
    checkReveal();
    const revealTimer = setTimeout(checkReveal, 200);

    // Counters
    const stats = D.getElementById("pw-stats");
    let counted = false;
    const runCount = () => {
      if (counted || !stats) return;
      const r = stats.getBoundingClientRect();
      if (r.top < (window.innerHeight || 0) * 0.85 && r.bottom > 0) {
        counted = true;
        stats.querySelectorAll<HTMLElement>("[data-count]").forEach((n) => {
          const target = parseFloat(n.getAttribute("data-count") || "0") || 0;
          const suffix = n.getAttribute("data-suffix") || "";
          const start = performance.now();
          const dur = 1600;
          const tick = (now: number) => {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            n.innerHTML = Math.round(target * eased) + suffix;
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      }
    };
    window.addEventListener("scroll", runCount, { passive: true });
    runCount();

    // Nav + progress
    const nav = D.getElementById("pw-nav");
    const prog = D.getElementById("pw-progress");
    const onScroll = () => {
      const y = window.scrollY || 0;
      if (nav) {
        if (y > 24) {
          nav.style.background = "rgba(255,255,255,.85)";
          nav.style.boxShadow = "0 8px 30px -16px rgba(20,40,90,.25)";
          nav.style.borderBottomColor = "rgba(10,11,16,.08)";
          nav.style.padding = "12px 32px";
        } else {
          nav.style.background = "rgba(255,255,255,.55)";
          nav.style.boxShadow = "none";
          nav.style.borderBottomColor = "transparent";
          nav.style.padding = "16px 32px";
        }
      }
      if (prog) {
        const h = D.documentElement.scrollHeight - window.innerHeight;
        prog.style.width = (h > 0 ? (y / h) * 100 : 0) + "%";
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    // Hero glow + tilt
    const hero = D.getElementById("pw-hero");
    const glow = D.getElementById("pw-glow");
    const tilt = D.querySelector<HTMLElement>("#pw-tilt-scene [data-tilt]");
    const heroMove = (ev: MouseEvent) => {
      if (!hero) return;
      const r = hero.getBoundingClientRect();
      const x = ev.clientX - r.left;
      const y = ev.clientY - r.top;
      if (glow) glow.style.transform = `translate(${x + 300}px,${y + 300}px)`;
      if (tilt) {
        const rx = ((y / r.height) - 0.5) * -10;
        const ry = ((x / r.width) - 0.5) * 12;
        tilt.style.transform = `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg)`;
      }
    };
    const heroLeave = () => {
      if (tilt) tilt.style.transform = "perspective(1000px) rotateX(0) rotateY(0)";
    };
    hero?.addEventListener("mousemove", heroMove);
    hero?.addEventListener("mouseleave", heroLeave);

    // Magnetic
    const magneticCleanups: Array<() => void> = [];
    D.querySelectorAll<HTMLElement>("[data-magnetic]").forEach((wrap) => {
      const btn = (wrap.firstElementChild as HTMLElement) || wrap;
      const move = (ev: MouseEvent) => {
        const r = wrap.getBoundingClientRect();
        const x = ev.clientX - r.left - r.width / 2;
        const y = ev.clientY - r.top - r.height / 2;
        btn.style.transform = `translate(${x * 0.3}px,${y * 0.4}px)`;
      };
      const leave = () => { btn.style.transform = "translate(0,0)"; };
      wrap.addEventListener("mousemove", move);
      wrap.addEventListener("mouseleave", leave);
      magneticCleanups.push(() => {
        wrap.removeEventListener("mousemove", move);
        wrap.removeEventListener("mouseleave", leave);
      });
    });

    // Mobile menu
    const burger = D.getElementById("pw-burger");
    const menu = D.getElementById("pw-mobile");
    const close = D.getElementById("pw-mclose");
    const open = () => { if (menu) menu.style.display = "flex"; };
    const hide = () => { if (menu) menu.style.display = "none"; };
    burger?.addEventListener("click", open);
    close?.addEventListener("click", hide);
    const mlinks = Array.from(D.querySelectorAll<HTMLElement>("[data-mlink]"));
    mlinks.forEach((l) => l.addEventListener("click", hide));

    // Form
    const form = D.getElementById("pw-form") as HTMLFormElement | null;
    const formRenderedAt = Date.now();
    const errBox = D.getElementById("pw-form-error") as HTMLElement | null;
    const submitBtn = form?.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    const modal = D.getElementById("pw-success-modal") as HTMLElement | null;
    const modalClose = D.getElementById("pw-success-close") as HTMLElement | null;
    const modalOk = D.getElementById("pw-success-ok") as HTMLElement | null;
    const showModal = () => {
      if (!modal) return;
      modal.style.display = "flex";
      document.body.style.overflow = "hidden";
    };
    const hideModal = () => {
      if (!modal) return;
      modal.style.display = "none";
      document.body.style.overflow = "";
    };
    modalClose?.addEventListener("click", hideModal);
    modalOk?.addEventListener("click", hideModal);
    modal?.addEventListener("click", (e) => { if (e.target === modal) hideModal(); });

    const onSubmit = async (ev: Event) => {
      ev.preventDefault();
      if (!form) return;
      if (errBox) { errBox.style.display = "none"; errBox.textContent = ""; }

      const fd = new FormData(form);
      const payload = {
        name: String(fd.get("name") ?? "").trim(),
        email: String(fd.get("email") ?? "").trim(),
        budget: String(fd.get("budget") ?? "").trim(),
        message: String(fd.get("message") ?? "").trim(),
        consent: fd.get("consent") === "1" || fd.get("consent") === "on",
        website: String(fd.get("website") ?? ""),
        elapsed_ms: Date.now() - formRenderedAt,
      };

      if (!payload.name || !payload.email || payload.message.length < 5) {
        if (errBox) {
          errBox.textContent = "Bitte fülle Name, E-Mail und Nachricht aus.";
          errBox.style.display = "block";
        }
        return;
      }
      if (!payload.consent) {
        if (errBox) {
          errBox.textContent = "Bitte stimme der Datenschutzerklärung zu.";
          errBox.style.display = "block";
        }
        return;
      }

      const originalLabel = submitBtn?.innerHTML;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.style.opacity = "0.7";
        submitBtn.style.cursor = "wait";
        submitBtn.innerHTML = "Wird gesendet…";
      }

      try {
        const res = await fetch("/api/public/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
        if (!res.ok || !data.ok) throw new Error(data?.error || "Senden fehlgeschlagen.");

        form.reset();
        if (submitBtn && originalLabel !== undefined) {
          submitBtn.disabled = false;
          submitBtn.style.opacity = "";
          submitBtn.style.cursor = "";
          submitBtn.innerHTML = originalLabel;
        }
        showModal();
      } catch (err) {
        if (errBox) {
          errBox.textContent =
            err instanceof Error ? err.message : "Beim Senden ist ein Fehler aufgetreten.";
          errBox.style.display = "block";
        }
        if (submitBtn && originalLabel !== undefined) {
          submitBtn.disabled = false;
          submitBtn.style.opacity = "";
          submitBtn.style.cursor = "";
          submitBtn.innerHTML = originalLabel;
        }
      }
    };
    form?.addEventListener("submit", onSubmit);


    // Portfolio: render from DB into placeholder grid
    const portfolioGrid = D.getElementById("pw-portfolio-grid");
    let portfolioCancelled = false;
    if (portfolioGrid) {
      const escape = (s: string) =>
        s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
      fetchPortfolio()
        .then((res) => {
          if (portfolioCancelled || !portfolioGrid) return;
          const items = res?.items ?? [];
          if (items.length === 0) {
            portfolioGrid.innerHTML = `<p style="grid-column:1/-1;text-align:center;color:#525a68;font-size:15px;padding:40px 0">Bald gibt es hier neue Projekte zu sehen.</p>`;
            return;
          }
          portfolioGrid.innerHTML = items
            .map((it) => {
              const imgHtml = it.image_url
                ? `<img class="pw-proj-img" src="${escape(it.image_url)}" alt="Logo ${escape(it.title)} – ${escape(it.tag)} Projekt von PIXIWEB" loading="lazy" decoding="async" width="800" height="520" style="position:absolute;inset:0;width:100%;height:100%;object-fit:contain;background:#fff;padding:34px;box-sizing:border-box;transition:transform .6s cubic-bezier(.16,1,.3,1)">`
                : `<div class="pw-proj-img" role="img" aria-label="${escape(it.title)}" style="position:absolute;inset:0;background:repeating-linear-gradient(45deg,#eef4fb,#eef4fb 14px,#e1ebf7 14px,#e1ebf7 28px);transition:transform .6s cubic-bezier(.16,1,.3,1)"></div>`;
              const hasUrl = Boolean(it.external_url?.trim());
              const urlAttr = hasUrl
                ? `href="${escape(it.external_url!)}" target="_blank" rel="noopener noreferrer" aria-label="${escape(it.title)} öffnen"`
                : `role="button" aria-label="${escape(it.title)} ansehen"`;
              return `
                <a class="pw-proj" ${urlAttr} style="display:block;background:#fff;border-radius:20px;overflow:hidden;box-shadow:0 18px 50px -32px rgba(20,40,90,.4);text-decoration:none;cursor:pointer">
                  <div style="position:relative;overflow:hidden;height:260px">
                    ${imgHtml}
                    <div class="pw-proj-overlay" style="position:absolute;inset:0;background:linear-gradient(135deg,rgba(46,139,255,.92),rgba(95,192,255,.92));opacity:0;transition:opacity .4s;display:flex;align-items:center;justify-content:center;color:#fff;font-size:18px;font-weight:700;gap:8px">Projekt ansehen →</div>
                  </div>
                  <div style="padding:26px 28px">
                    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px">
                      <span style="font-size:12px;font-weight:600;color:#fff;background:#1f6fe0;padding:5px 11px;border-radius:100px">${escape(it.tag)}</span>
                    </div>
                    <h3 style="font-size:23px;font-weight:700;letter-spacing:-.02em;margin:0 0 6px;color:#0a0b10">${escape(it.title)}</h3>
                  </div>
                </a>`;
            })
            .join("");
          // Trigger reveal recheck for any new elements (cards don't use data-reveal here)
          checkReveal();
        })
        .catch((e) => {
          console.error("portfolio fetch failed", e);
        });
    }

    return () => {
      portfolioCancelled = true;
      clearTimeout(revealTimer);

      window.removeEventListener("scroll", checkReveal);
      window.removeEventListener("resize", checkReveal);
      window.removeEventListener("scroll", runCount);
      window.removeEventListener("scroll", onScroll);
      hero?.removeEventListener("mousemove", heroMove);
      hero?.removeEventListener("mouseleave", heroLeave);
      magneticCleanups.forEach((c) => c());
      burger?.removeEventListener("click", open);
      close?.removeEventListener("click", hide);
      mlinks.forEach((l) => l.removeEventListener("click", hide));
      form?.removeEventListener("submit", onSubmit);
    };
  }, []);

  return <div ref={ref} dangerouslySetInnerHTML={{ __html: PIXIWEB_HTML }} />;
}
